// src/components/editor/NavigationPanel.jsx
//
// Document outline / navigation tree. Mirrors the editor's heading blocks and
// lets the author jump to, insert, delete, re-level, and reorder chapters.
// Reorder has three paths: drag the grip handle (any distance, panel auto-
// scrolls), the inline ▲/▼ arrows (one step, no menu), or the ⋮ menu.
//
// NOTE: the panel container (.sidebar-panel-vertical) ships aggressive global
// CSS that forces any MUI Button/IconButton to full width with gold styling, so
// we render controls as plain <Box> elements those rules don't target. The MUI
// <Menu> is a portal, so it's unaffected.
import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Menu, MenuItem, Divider } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const GOLD = '#F5D76E';
const PURPLE = '#5a4d99';

// Small hover-revealed control (arrows, menu trigger). Plain Box so the panel's
// global MUI button CSS doesn't hijack it.
function RowControl({ onClick, disabled, active, title, children }) {
  return (
    <Box
      component="span"
      title={title}
      onClick={(e) => { e.stopPropagation(); if (!disabled) onClick?.(e); }}
      sx={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, width: 20, height: 20, borderRadius: '4px',
        cursor: disabled ? 'default' : 'pointer',
        color: active ? PURPLE : GOLD,
        opacity: disabled ? 0.25 : 1,
        '&:hover': { backgroundColor: disabled ? 'transparent' : 'rgba(0,0,0,0.12)' },
      }}
    >
      {children}
    </Box>
  );
}

// Inline title editor shown in place of the row's text while renaming. Owns its
// own draft state (mounted only during edit). Commits on Enter/blur, cancels on
// Escape; a guard ref ensures the final blur after Enter/Escape doesn't re-fire.
function RowTitleEditor({ initial, isActive, onCommit, onCancel }) {
  const [value, setValue] = useState(initial);
  const inputRef = useRef(null);
  const doneRef = useRef(false);

  useEffect(() => {
    const el = inputRef.current;
    if (el) { el.focus(); el.select(); }
  }, []);

  const finish = (save) => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (save) onCommit(value);
    else onCancel();
  };

  return (
    <Box
      component="input"
      ref={inputRef}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') { e.preventDefault(); finish(true); }
        else if (e.key === 'Escape') { e.preventDefault(); finish(false); }
      }}
      onBlur={() => finish(true)}
      sx={{
        flex: 1, minWidth: 0, font: 'inherit', fontSize: 14,
        color: isActive ? PURPLE : 'white',
        backgroundColor: isActive ? 'rgba(0,0,0,0.12)' : 'rgba(0,0,0,0.25)',
        border: `1px solid ${GOLD}`, borderRadius: '4px',
        px: 0.5, py: '2px', outline: 'none',
      }}
    />
  );
}

// One row in the outline tree, rendered recursively for nested sub-headings.
function OutlineNode({ node, index, siblings, activeBlockId, editingId, wordCounts, onJump, onOpenMenu, onMove, onStartEdit, onCommitEdit, onCancelEdit, depth, drag, collapsedIds, onToggleCollapse, renderAfterRow }) {
  const rowRef = useRef(null);
  const isActive = node.blockId && node.blockId === activeBlockId;
  const isEditing = node.blockId && node.blockId === editingId;
  const isDragging = drag?.dragId === node.blockId;
  const dropHint =
    drag?.dropTarget?.blockId === node.blockId ? drag.dropTarget.position : null;
  const canUp = index > 0;
  const canDown = index < siblings.length - 1;
  const hasChildren = !!node.children?.length;
  const isCollapsed = hasChildren && collapsedIds?.has(node.blockId);

  return (
    <>
      <Box
        ref={rowRef}
        onDragOver={(e) => drag?.onDragOver(e, node.blockId)}
        onDrop={(e) => drag?.onDrop(e, node.blockId)}
        onDragEnd={() => drag?.onDragEnd()}
        onClick={() => { if (!isEditing) onJump?.(node.blockId); }}
        title={node.text}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          cursor: 'pointer',
          borderRadius: '8px',
          pr: 0.5,
          pl: `${depth * 12}px`,
          py: 0.5,
          minWidth: 0,
          color: isActive ? PURPLE : 'white',
          backgroundColor: isActive ? GOLD : 'transparent',
          fontWeight: node.level === 1 ? 600 : 400,
          opacity: isDragging ? 0.4 : 1,
          boxShadow:
            dropHint === 'before' ? `inset 0 2px 0 ${GOLD}` :
            dropHint === 'after' ? `inset 0 -2px 0 ${GOLD}` : 'none',
          transition: 'background-color 0.15s ease',
          '&:hover': { backgroundColor: isActive ? GOLD : 'rgba(245,215,110,0.15)' },
          '&:hover .rv-outline-actions': { opacity: 1 },
        }}
      >
        {/* Expand/collapse chevron — always visible (not hover-gated) when the
            chapter has demoted children underneath it; a same-size invisible
            spacer otherwise so sibling rows stay aligned. */}
        <Box
          component="span"
          onClick={(e) => { e.stopPropagation(); if (hasChildren) onToggleCollapse?.(node.blockId); }}
          title={hasChildren ? (isCollapsed ? 'Expand' : 'Collapse') : undefined}
          sx={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, width: 16, height: 16, borderRadius: '4px',
            cursor: hasChildren ? 'pointer' : 'default',
            color: isActive ? PURPLE : GOLD,
            visibility: hasChildren ? 'visible' : 'hidden',
            '&:hover': { backgroundColor: hasChildren ? 'rgba(0,0,0,0.12)' : 'transparent' },
          }}
        >
          {hasChildren && (isCollapsed
            ? <ChevronRightIcon sx={{ fontSize: 16 }} />
            : <ExpandMoreIcon sx={{ fontSize: 16 }} />)}
        </Box>

        {/* Drag grip — the only draggable part, so it never fights click-to-jump */}
        <Box
          component="span"
          className="rv-outline-actions"
          draggable={!!node.blockId && !isEditing}
          onDragStart={(e) => drag?.onDragStart(e, node.blockId, rowRef.current)}
          onClick={(e) => e.stopPropagation()}
          title="Drag to reorder"
          sx={{
            display: 'inline-flex', alignItems: 'center', flexShrink: 0,
            opacity: 0, cursor: 'grab', color: isActive ? PURPLE : 'rgba(255,255,255,0.6)',
            transition: 'opacity 0.15s ease', '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 16 }} />
        </Box>

        {node.label != null && (
          <Typography
            component="span"
            sx={{ color: isActive ? PURPLE : GOLD, fontSize: 11, fontWeight: 700, flexShrink: 0 }}
          >
            {node.label}.
          </Typography>
        )}
        {isEditing ? (
          <RowTitleEditor
            initial={node.text}
            isActive={isActive}
            onCommit={(text) => onCommitEdit?.(node.blockId, text)}
            onCancel={() => onCancelEdit?.()}
          />
        ) : (
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Typography
              component="span"
              noWrap
              onDoubleClick={(e) => { e.stopPropagation(); if (node.blockId) onStartEdit?.(node.blockId); }}
              title="Double-click to rename"
              sx={{ fontSize: node.level === 1 ? 14 : 13, overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {node.text}
            </Typography>
            {node.blockId && wordCounts?.[node.blockId] != null && (
              <Typography
                component="span"
                sx={{
                  fontSize: 10, lineHeight: 1.2, mt: '1px',
                  color: isActive ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.5)',
                }}
              >
                {wordCounts[node.blockId].toLocaleString()} {wordCounts[node.blockId] === 1 ? 'word' : 'words'}
              </Typography>
            )}
          </Box>
        )}

        <Box className="rv-outline-actions" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, flexShrink: 0, opacity: 0, transition: 'opacity 0.15s ease' }}>
          <RowControl title="Move up" disabled={!canUp} active={isActive} onClick={() => onMove?.(node, index, siblings, 'up')}>
            <ArrowUpwardIcon sx={{ fontSize: 15 }} />
          </RowControl>
          <RowControl title="Move down" disabled={!canDown} active={isActive} onClick={() => onMove?.(node, index, siblings, 'down')}>
            <ArrowDownwardIcon sx={{ fontSize: 15 }} />
          </RowControl>
          <RowControl title="More" active={isActive} onClick={(e) => onOpenMenu?.(e.currentTarget, node)}>
            <MoreVertIcon sx={{ fontSize: 16 }} />
          </RowControl>
        </Box>
      </Box>

      {/* Slot for whatever the host wants under this row — the Story Elements
          panel puts a chapter's relationships here, so a chapter expands in
          place like every other element. Sits above the children so an
          expanded parent doesn't push its detail below its own subtree. */}
      {renderAfterRow?.(node)}

      {!isCollapsed && node.children?.map((child, ci) => (
        <OutlineNode
          key={child.blockId || child.text}
          node={child}
          index={ci}
          siblings={node.children}
          activeBlockId={activeBlockId}
          editingId={editingId}
          wordCounts={wordCounts}
          onJump={onJump}
          onOpenMenu={onOpenMenu}
          onMove={onMove}
          onStartEdit={onStartEdit}
          onCommitEdit={onCommitEdit}
          onCancelEdit={onCancelEdit}
          depth={depth + 1}
          drag={drag}
          collapsedIds={collapsedIds}
          onToggleCollapse={onToggleCollapse}
          renderAfterRow={renderAfterRow}
        />
      ))}
    </>
  );
}

const NavigationPanel = ({
  outline = [],
  wordCounts = {},
  activeBlockId,
  onJump,
  onInsertAfter,
  onRename,
  onDelete,
  onPromote,
  onDemote,
  onReorder,
  // Set by StoryElementsPanel, which renders this as its Chapters section: the
  // panel body is the one scroll container, so drag auto-scroll has to move
  // that element rather than this one. Defaults to this panel scrolling itself.
  scrollRef = null,
  // Optional slot rendered under each chapter row — see OutlineNode.
  renderAfterRow = null,
}) => {
  const [menu, setMenu] = useState(null); // { anchorEl, node }
  const [editingId, setEditingId] = useState(null); // blockId being renamed
  const [dragId, setDragId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // { blockId, position }
  const [collapsedIds, setCollapsedIds] = useState(() => new Set()); // blockIds hidden
  const toggleCollapse = (blockId) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(blockId)) next.delete(blockId);
      else next.add(blockId);
      return next;
    });
  };
  const containerRef = useRef(null);
  const autoScrollRef = useRef(0); // -1 up, 1 down, 0 idle
  const rafRef = useRef(null);

  const openMenu = (anchorEl, node) => setMenu({ anchorEl, node });
  const closeMenu = () => setMenu(null);
  const run = (fn) => () => { fn?.(menu?.node?.blockId); closeMenu(); };

  // Rename: enter inline-edit on a row. From the ⋮ menu we close it first, then
  // start editing the menu's node; double-click on the title starts it directly.
  const startEdit = (blockId) => { if (blockId) setEditingId(blockId); };
  const commitEdit = (blockId, text) => { onRename?.(blockId, text); setEditingId(null); };
  const cancelEdit = () => setEditingId(null);
  const startEditFromMenu = () => { const id = menu?.node?.blockId; closeMenu(); startEdit(id); };

  const canPromote = (menu?.node?.level ?? 1) > 1;
  const canDemote = (menu?.node?.level ?? 4) < 4;

  // Reorder one step among same-level siblings (used by the inline arrows and
  // the menu). Tree children are exactly same-level, same-parent siblings.
  const move = (node, index, siblings, dir) => {
    if (dir === 'up' && index > 0) onReorder?.(node.blockId, siblings[index - 1].blockId, 'before');
    if (dir === 'down' && index < siblings.length - 1) onReorder?.(node.blockId, siblings[index + 1].blockId, 'after');
  };

  // Auto-scroll the panel while dragging near its top/bottom edge, so a chapter
  // can be dragged past many others even when the list overflows the panel.
  // Whichever element actually scrolls: this panel standalone, or the Story
  // Elements panel body when the outline is one section inside it.
  const scrollEl = () => scrollRef?.current || containerRef.current;
  const stopAutoScroll = () => {
    autoScrollRef.current = 0;
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  };
  const tickAutoScroll = () => {
    const el = scrollEl();
    if (el && autoScrollRef.current) {
      el.scrollTop += autoScrollRef.current * 10;
      rafRef.current = requestAnimationFrame(tickAutoScroll);
    } else {
      rafRef.current = null;
    }
  };
  const onContainerDragOver = (e) => {
    if (!dragId) return;
    const el = scrollEl();
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const zone = 48;
    let dir = 0;
    if (e.clientY < rect.top + zone) dir = -1;
    else if (e.clientY > rect.bottom - zone) dir = 1;
    autoScrollRef.current = dir;
    if (dir && !rafRef.current) rafRef.current = requestAnimationFrame(tickAutoScroll);
    if (!dir) stopAutoScroll();
  };

  // Native HTML5 drag-and-drop. Dragging starts from the grip; dropping in a
  // row's top half places the dragged chapter before it, bottom half after.
  const drag = {
    dragId,
    dropTarget,
    onDragStart: (e, blockId, rowEl) => {
      if (!blockId) return;
      setDragId(blockId);
      e.dataTransfer.effectAllowed = 'move';
      try { e.dataTransfer.setData('text/plain', blockId); } catch { /* ignore */ }
      // Use the whole row as the drag image, not just the tiny grip.
      try { if (rowEl) e.dataTransfer.setDragImage(rowEl, 12, 12); } catch { /* ignore */ }
    },
    onDragOver: (e, blockId) => {
      if (!dragId || blockId === dragId) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      const rect = e.currentTarget.getBoundingClientRect();
      const position = e.clientY - rect.top < rect.height / 2 ? 'before' : 'after';
      setDropTarget((prev) =>
        prev?.blockId === blockId && prev?.position === position ? prev : { blockId, position }
      );
    },
    onDrop: (e, blockId) => {
      e.preventDefault();
      if (dragId && blockId && dragId !== blockId && dropTarget) {
        onReorder?.(dragId, blockId, dropTarget.position);
      }
      setDragId(null);
      setDropTarget(null);
      stopAutoScroll();
    },
    onDragEnd: () => { setDragId(null); setDropTarget(null); stopAutoScroll(); },
  };

  return (
    <Box
      ref={containerRef}
      onDragOver={onContainerDragOver}
      sx={{
        display: 'flex', flexDirection: 'column', gap: 0.5, color: 'white',
        // Embedded as a section, the parent owns the padding and the scrolling.
        ...(scrollRef
          ? { px: 2, pb: 1 }
          : { p: 2, height: '100%', overflowY: 'auto' }),
      }}
    >

      {outline.length === 0 ? (
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, mt: 1, lineHeight: 1.5 }}>
          No headings yet. Use the Format tab to style text as <b>Heading 1–4</b>, or pick
          <b> Add &rsaquo; Chapter</b> above. Your chapters will appear here.
        </Typography>
      ) : (
        outline.map((node, i) => (
          <OutlineNode
            key={node.blockId || node.text}
            node={node}
            index={i}
            siblings={outline}
            activeBlockId={activeBlockId}
            editingId={editingId}
            wordCounts={wordCounts}
            onJump={onJump}
            onOpenMenu={openMenu}
            onMove={move}
            onStartEdit={startEdit}
            onCommitEdit={commitEdit}
            onCancelEdit={cancelEdit}
            depth={0}
            drag={drag}
            collapsedIds={collapsedIds}
            onToggleCollapse={toggleCollapse}
            renderAfterRow={renderAfterRow}
          />
        ))
      )}

      <Menu anchorEl={menu?.anchorEl} open={!!menu} onClose={closeMenu}>
        <MenuItem onClick={startEditFromMenu}>
          <EditOutlinedIcon sx={{ fontSize: 18, mr: 1 }} /> Rename chapter
        </MenuItem>
        <MenuItem onClick={run(onInsertAfter)}>
          <PlaylistAddIcon sx={{ fontSize: 18, mr: 1 }} /> Insert chapter after
        </MenuItem>
        <MenuItem onClick={run(onPromote)} disabled={!canPromote}>
          <KeyboardArrowLeftIcon sx={{ fontSize: 18, mr: 1 }} /> Promote (higher level)
        </MenuItem>
        <MenuItem onClick={run(onDemote)} disabled={!canDemote}>
          <KeyboardArrowRightIcon sx={{ fontSize: 18, mr: 1 }} /> Demote (lower level)
        </MenuItem>
        <Divider />
        <MenuItem onClick={run(onDelete)} sx={{ color: '#d32f2f' }}>
          <DeleteOutlineIcon sx={{ fontSize: 18, mr: 1 }} /> Delete chapter
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default NavigationPanel;
